import React from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import { useFormBuilderEditorContext } from "../../../formBuilderEditorContext";
import { useFormBuilderDebouncedUpdate } from "../../../hooks/debouncedUpdate/useFormBuilderDebouncedUpdate";
import { IFormBuilderTextarea, TFormBuilderTextareaSettings } from "../../../model/item/formBuilderTextarea";

interface IFormBuilderTextareaSettingsProps {
  item: IFormBuilderTextarea;
}

export function FormBuilderTextareaSettings(props: IFormBuilderTextareaSettingsProps) {
  const { item } = props;

  const { t } = useTranslation();

  const { formBuilderController } = useFormBuilderEditorContext();

  const settings = item.data.settings;

  const debouncedLabel = useFormBuilderDebouncedUpdate<string>({
    defaultValue: "",
    value: settings.label,
  });

  const debouncedPlaceholder = useFormBuilderDebouncedUpdate<string>({
    defaultValue: "",
    value: settings.placeholder,
  });

  function dispatchSettings(partialNextSettings: Partial<TFormBuilderTextareaSettings>) {
    formBuilderController.updateItemSettings({
      key: item.key,
      prevItemSettings: {
        type: item.data.type,
        settings: cloneDeep(settings),
      },
      nextItemSettings: {
        type: item.data.type,
        settings: {
          ...cloneDeep(settings),
          ...partialNextSettings,
        },
      },
    });
  }

  return (
    <>
      <Input
        label={t("Label")}
        value={debouncedLabel.state.active ? debouncedLabel.state.value : settings.label}
        onFocus={debouncedLabel.activate}
        onChange={e =>
          debouncedLabel.change(e.target.value, label => {
            dispatchSettings({ label });
          })
        }
        onBlur={() =>
          debouncedLabel.commit(label => {
            dispatchSettings({ label });
          })
        }
      />

      <Input
        label={t("Placeholder")}
        value={debouncedPlaceholder.state.active ? debouncedPlaceholder.state.value : settings.placeholder}
        onFocus={debouncedPlaceholder.activate}
        onChange={e =>
          debouncedPlaceholder.change(e.target.value, placeholder => {
            dispatchSettings({ placeholder });
          })
        }
        onBlur={() =>
          debouncedPlaceholder.commit(placeholder => {
            dispatchSettings({ placeholder });
          })
        }
      />

      <Checkbox
        size="medium"
        label={t("Required")}
        checked={settings.required}
        onChange={e => {
          const required = e.target.checked;

          if (required !== settings.required) {
            dispatchSettings({ required });
          }
        }}
      />
    </>
  );
}
